export const SIDEBAR_ACTION_ITEM_HEIGHT = 36;
export const SIDEBAR_ACTION_BAR_PADDING_Y = 16;
export const SIDEBAR_ACTION_BAR_GAP = 4;
export const SIDEBAR_SEPARATOR_HEIGHT = 1;
export const SIDEBAR_COMPACT_ACTION_BAR_ITEM_COUNT = 1;

export interface SidebarMiddleBudgetInput {
  availableHeight: number;
  headerHeight: number;
  footerHeight: number;
  actionBarItemCount: number;
  separatorCount?: number;
}

export interface SidebarActionsFitInput {
  availableHeight: number;
  headerHeight: number;
  footerHeight: number;
  contentHeight: number;
  fullActionBarItemCount: number;
  separatorCount?: number;
}

export interface SidebarContentScrollInput {
  availableHeight: number;
  headerHeight: number;
  footerHeight: number;
  contentHeight: number;
  actionsMode: 'full' | 'compact';
  fullActionBarItemCount: number;
  separatorCount?: number;
}

export function estimateFullActionBarHeight(itemCount: number): number {
  if (itemCount <= 0) {
    return 0;
  }

  return (
    SIDEBAR_ACTION_BAR_PADDING_Y +
    itemCount * SIDEBAR_ACTION_ITEM_HEIGHT +
    (itemCount - 1) * SIDEBAR_ACTION_BAR_GAP
  );
}

export function computeMiddleBudget({
  availableHeight,
  headerHeight,
  footerHeight,
  actionBarItemCount,
  separatorCount = 0,
}: SidebarMiddleBudgetInput): number {
  const separatorHeight = separatorCount * SIDEBAR_SEPARATOR_HEIGHT;
  const actionBarHeight = estimateFullActionBarHeight(actionBarItemCount);

  return availableHeight - headerHeight - footerHeight - separatorHeight - actionBarHeight;
}

export function measureContentHeight(content: HTMLElement): number {
  const styles = getComputedStyle(content);
  const gap = Number.parseFloat(styles.rowGap || styles.gap) || 0;
  const children = Array.from(content.children) as HTMLElement[];

  if (children.length === 0) {
    return 0;
  }

  return children.reduce((height, child, index) => {
    return height + child.offsetHeight + (index > 0 ? gap : 0);
  }, 0);
}

export function sidebarNeedsCompactActions({
  availableHeight,
  headerHeight,
  contentHeight,
  fullActionBarItemCount,
  footerHeight,
  separatorCount = 0,
}: SidebarActionsFitInput): boolean {
  if (availableHeight <= 0) {
    return false;
  }

  const middleBudget = computeMiddleBudget({
    availableHeight,
    headerHeight,
    footerHeight,
    actionBarItemCount: fullActionBarItemCount,
    separatorCount,
  });

  return contentHeight > middleBudget;
}

export function sidebarContentNeedsScroll({
  availableHeight,
  headerHeight,
  contentHeight,
  footerHeight,
  actionsMode,
  fullActionBarItemCount,
  separatorCount = 0,
}: SidebarContentScrollInput): boolean {
  if (availableHeight <= 0) {
    return false;
  }

  const actionBarItemCount =
    actionsMode === 'compact'
      ? SIDEBAR_COMPACT_ACTION_BAR_ITEM_COUNT
      : fullActionBarItemCount;

  const middleBudget = computeMiddleBudget({
    availableHeight,
    headerHeight,
    footerHeight,
    actionBarItemCount,
    separatorCount,
  });

  return contentHeight > middleBudget;
}
